import React, { useState, useEffect, useCallback } from 'react';
import { FileText, CheckSquare, Download, Tag, Clock, Users, ChevronDown, ChevronRight, Shield, Star, X as XIcon } from 'lucide-react';
import { getRecordFiles, getTaskNotes, getTaskHistory, getUiPreferences, getRecordTeamAccess, revokeRecordTeamAccess } from '../api/chatAI';
import api from '../api/client';
import PinnedFieldsSection from './PinnedFieldsSection';
import LeftPanelAccordion from './LeftPanelAccordion';
import TeamAccessModal from './TeamAccessModal';

function relativeTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function CaseProfile({ profile, onSelectRecord, onSendMessage }) {
  // `undefined` = preference not yet loaded; null = explicitly collapsed;
  // string = which section should open on mount.
  const [savedOpenSection, setSavedOpenSection] = useState(undefined);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showCommsModal, setShowCommsModal] = useState(false);
  const [commsSearch, setCommsSearch] = useState('');
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [taskNotes, setTaskNotes] = useState({});
  const [loadingNotes, setLoadingNotes] = useState({});
  const [taskHistory, setTaskHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [historyModalTask, setHistoryModalTask] = useState(null);
  const [parties, setParties] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);
  const [teamAccessList, setTeamAccessList] = useState(null);
  const [teamAccessLoading, setTeamAccessLoading] = useState(false);
  const [myAccessLevel, setMyAccessLevel] = useState(null);
  const [showTeamAccessModal, setShowTeamAccessModal] = useState(false);

  const handleMarkRead = async (commId) => {
    try {
      await fetch(`/api/chat-ai/comms/mark-read/${commId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('lf_token')}` }
      });
    } catch (e) { /* non-blocking */ }
  };

  const fetchTaskNotes = async (taskId) => {
    if (taskNotes[taskId] || loadingNotes[taskId]) return;
    setLoadingNotes(prev => ({ ...prev, [taskId]: true }));
    try {
      const data = await getTaskNotes(taskId);
      setTaskNotes(prev => ({ ...prev, [taskId]: data.notes || [] }));
    } catch(e) { console.error('[TaskNotes] fetch error:', e); }
    finally { setLoadingNotes(prev => ({ ...prev, [taskId]: false })); }
  };

  const fetchTaskHistory = async (taskId) => {
    if (taskHistory[taskId] || loadingHistory[taskId]) return;
    setLoadingHistory(prev => ({ ...prev, [taskId]: true }));
    try {
      const data = await getTaskHistory(taskId);
      setTaskHistory(prev => ({ ...prev, [taskId]: data.history || [] }));
    } catch(e) { console.error('[TaskHistory] fetch error:', e); }
    finally { setLoadingHistory(prev => ({ ...prev, [taskId]: false })); }
  };

  // Load server-persisted open section once per user; resets on record switch.
  useEffect(() => {
    let cancelled = false;
    getUiPreferences()
      .then((prefs) => {
        if (cancelled) return;
        // null is a valid persisted value meaning "all collapsed"; undefined
        // here just means the key was never saved — default to tasks.
        const val = prefs && Object.prototype.hasOwnProperty.call(prefs, 'left_panel_open_section')
          ? prefs.left_panel_open_section
          : 'tasks';
        setSavedOpenSection(val);
      })
      .catch(() => { if (!cancelled) setSavedOpenSection('tasks'); });
    return () => { cancelled = true; };
  }, []);

  // Pre-fetch parties on record change — the parties section no longer lazy-loads
  // because the accordion owns its open state. Cheap idempotent call.
  useEffect(() => {
    let cancelled = false;
    if (!profile.record_id) return;
    setLoadingParties(true);
    api.get(`/parties/${profile.record_id}`)
      .then((res) => { if (!cancelled) setParties(res.data.parties || []); })
      .catch(() => { if (!cancelled) setParties([]); })
      .finally(() => { if (!cancelled) setLoadingParties(false); });
    return () => { cancelled = true; };
  }, [profile.record_id]);

  const fetchTeamAccess = useCallback(() => {
    if (!profile.record_id) return;
    setTeamAccessLoading(true);
    getRecordTeamAccess(profile.record_id)
      .then(data => {
        setTeamAccessList(data.teams || []);
        setMyAccessLevel(data.my_access_level || null);
      })
      .catch(() => { setTeamAccessList([]); setMyAccessLevel(null); })
      .finally(() => setTeamAccessLoading(false));
  }, [profile.record_id]);

  // Listen for teamAccessChanged events (from TeamAccessModal or AI confirm card)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.record_id === profile.record_id) fetchTeamAccess();
    };
    window.addEventListener('teamAccessChanged', handler);
    return () => window.removeEventListener('teamAccessChanged', handler);
  }, [profile.record_id, fetchTeamAccess]);

  const handleRevokeTeamAccess = async (teamId, teamName) => {
    if (!window.confirm(`Remove ${teamName} from this record?`)) return;
    try {
      await revokeRecordTeamAccess(profile.record_id, teamId, 'manual');
      window.dispatchEvent(new CustomEvent('teamAccessChanged', { detail: { record_id: profile.record_id } }));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to revoke access');
    }
  };

  const openDocsModal = () => {
    setShowDocsModal(true);
    if (files.length === 0 && !loadingFiles) {
      setLoadingFiles(true);
      getRecordFiles(profile.record_id)
        .then(data => setFiles(data.files || []))
        .catch(() => setFiles([]))
        .finally(() => setLoadingFiles(false));
    }
  };

  useEffect(() => {
    setTaskNotes({});
    setTaskHistory({});
    setExpandedTaskId(null);
    setFiles([]);
    setParties([]);
    setTeamAccessList(null);
    setMyAccessLevel(null);
    setShowTeamAccessModal(false);
    setShowDocsModal(false);
    setShowCommsModal(false);
  }, [profile.record_id]);

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  const initials = profile.title
    ? profile.title.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : '?';

  const overdueCount = (profile.open_tasks || []).filter(t => t.due_date && new Date(t.due_date) < new Date()).length;
  const unreadCount = (profile.comms || []).filter(c => !c.is_read && c.direction === 'inbound').length;

  // Build the accordion sections — Tasks, Parties, Pinned Fields.
  // The render functions hold the same JSX that used to live inline in the
  // render tree so UX is identical; only the header/toggling machinery moved.
  const accordionSections = [
    {
      id: 'tasks',
      label: 'Tasks',
      badge: profile.open_tasks?.length || 0,
      warning: overdueCount > 0 ? `⚠️ ${overdueCount} overdue` : null,
      badgeColor: overdueCount > 0 ? '#450a0a' : undefined,
      render: () => (
        <div className="px-3 pb-2 space-y-2">
          {(profile.open_tasks || []).length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-3">No open tasks</p>
          ) : (
            (profile.open_tasks || []).map((t, i) => {
              const taskKey = t.id || i;
              const isExpanded = expandedTaskId === taskKey;
              const isOverdue = t.due_date && new Date(t.due_date) < new Date();
              return (
                <div key={taskKey} className="bg-gray-800/60 rounded-xl overflow-hidden transition-all">
                  <div className="p-3 cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => { const next = isExpanded ? null : taskKey; setExpandedTaskId(next); if (next && t.id) { fetchTaskNotes(t.id); fetchTaskHistory(t.id); } }}>
                    <div className="flex items-start gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${t.priority === 'high' || t.priority === 'urgent' ? 'bg-red-400' : t.priority === 'normal' ? 'bg-blue-400' : 'bg-gray-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-gray-200 text-xs font-medium truncate">{t.task_number ? `#${t.task_number} - ${t.title}` : t.title}</p>
                          {t.recurrence_rule && <span className="text-[10px] text-purple-400 flex-shrink-0">🔄</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.status === 'pending' ? 'bg-yellow-900/40 text-yellow-400' : t.status === 'in_progress' ? 'bg-blue-900/40 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>{t.status}</span>
                          {t.due_date && <span className={`text-[10px] ${isOverdue ? 'text-red-400' : 'text-gray-500'}`}>{isOverdue ? '⚠ Overdue · ' : 'Due '}{formatDate(t.due_date)}</span>}
                          <span className={`text-[10px] font-medium ${t.priority === 'high' ? 'text-red-400' : t.priority === 'low' ? 'text-gray-500' : 'text-blue-400'}`}>{(t.priority || 'normal').toUpperCase()}</span>
                        </div>
                      </div>
                      <span className="text-gray-600 text-[10px] flex-shrink-0 mt-0.5">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-gray-700/50 px-3 py-2">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Notes</p>
                        <button onClick={(e) => { e.stopPropagation(); setHistoryModalTask({ ...t, history: taskHistory[t.id] || [] }); }}
                          className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-purple-300 transition-colors">
                          <Clock className="h-3 w-3" />
                          <span>History {taskHistory[t.id]?.length > 0 ? `(${taskHistory[t.id].length})` : ''}</span>
                        </button>
                      </div>
                      <div className="max-h-56 overflow-y-auto pr-1 space-y-2">
                        {loadingNotes[t.id] ? <p className="text-xs text-gray-600 italic">Loading notes...</p>
                          : (taskNotes[t.id] || []).length === 0 ? <p className="text-xs text-gray-600 italic">No notes on this task</p>
                          : (taskNotes[t.id] || []).map((note, ni) => (
                            <div key={note.id || ni} className="bg-gray-900/80 rounded-lg px-2.5 py-2 border border-gray-800/60">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] font-medium text-purple-300">{note.first_name ? `${note.first_name} ${note.last_name || ''}`.trim() : note.email}</span>
                                <span className="text-[10px] text-gray-600">{new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                              </div>
                              <p className="text-xs text-gray-200 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ),
    },
    {
      id: 'parties',
      label: 'Parties',
      badge: profile.parties?.length || parties.length || 0,
      render: () => (
        <div className="px-3 pb-2 space-y-2">
          {loadingParties ? <p className="text-xs text-gray-500 text-center py-3">Loading...</p>
            : (parties.length === 0 && (profile.parties || []).length === 0) ? <p className="text-xs text-gray-500 text-center py-3">No parties linked</p>
            : (parties.length > 0 ? parties : profile.parties || []).map((p) => (
              <div key={p.relation_id} className="bg-gray-800/60 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-purple-800 flex items-center justify-center text-[10px] font-bold text-purple-200 flex-shrink-0">
                    {(p.contact_name || '?').split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-200 text-xs font-semibold">{p.contact_name || p.name}</span>
                      {p.is_primary && <span className="text-[9px] text-amber-400">★</span>}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {p.reference_number && <span className="font-mono">{p.reference_number} · </span>}
                      {p.role && <span className="text-gray-400">{p.role}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ),
    },
  ];

  accordionSections.push({
    id: 'team_access',
    label: 'Team Access',
    badge: teamAccessList?.length || 0,
    onOpen: () => { if (teamAccessList === null) fetchTeamAccess(); },
    render: () => (
      <div className="px-3 pb-2 space-y-2">
        {teamAccessLoading ? (
          <p className="text-xs text-gray-500 text-center py-3">Loading...</p>
        ) : (!teamAccessList || teamAccessList.length === 0) ? (
          <p className="text-xs text-gray-500 text-center py-3">No teams have access</p>
        ) : (
          teamAccessList.map(t => {
            const levelColors = { read: '#6b7280', write: '#3b82f6', full: '#c9a84c' };
            const viaLabels = { stage_transition: 'via stage owner', manual: 'manual share', creator: 'creator', intake_convert: 'intake convert', migration: 'migration', stage_owner: 'via stage owner' };
            return (
              <div key={t.team_id} className="bg-gray-800/60 rounded-xl p-3 flex items-center gap-2">
                {t.is_primary && <Star className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#c9a84c' }} />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-200 text-xs font-semibold">{t.team_name}</span>
                    {t.is_primary && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: '#1a1710', color: '#c9a84c', border: '1px solid #3d3418' }}>Primary</span>
                    )}
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: levelColors[t.access_level] + '22', color: levelColors[t.access_level] }}>{t.access_level}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {viaLabels[t.granted_via] || t.granted_via}
                    {t.granted_at && ` · granted ${relativeTime(t.granted_at)}`}
                  </div>
                </div>
                {!t.is_primary && myAccessLevel === 'full' && (
                  <button onClick={() => handleRevokeTeamAccess(t.team_id, t.team_name)}
                    className="p-1 text-gray-600 hover:text-red-400 transition-colors flex-shrink-0" title="Revoke access">
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}
        {myAccessLevel === 'full' && (
          <button onClick={() => setShowTeamAccessModal(true)}
            className="w-full text-center text-xs py-2 rounded-lg transition-colors hover:bg-gray-800"
            style={{ color: '#c9a84c', border: '1px dashed #3d3418' }}>
            + Grant team access
          </button>
        )}
        {myAccessLevel && myAccessLevel !== 'full' && (
          <p className="text-[10px] text-gray-600 text-center py-1" title="You need full access to share this case">
            🔒 Full access required to grant/revoke teams
          </p>
        )}
      </div>
    ),
  });

  if (profile.record_type_id) {
    accordionSections.push({
      id: 'fields',
      label: 'Pinned Fields',
      render: () => (
        <div className="mx-3 mb-2 bg-gray-800/60 rounded-xl p-3">
          <PinnedFieldsSection
            recordTypeId={profile.record_type_id}
            orderedFields={profile.ordered_fields || []}
            fieldData={profile.field_data || {}}
          />
        </div>
      ),
    });
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 text-center border-b border-gray-800 flex-shrink-0">
        <div className="w-14 h-14 rounded-full bg-purple-800 flex items-center justify-center text-lg font-bold text-purple-200 mx-auto mb-2">
          {initials}
        </div>
        <h2 className="text-white font-semibold text-sm">{profile.title}</h2>
        <p className="text-gray-400 text-xs mt-0.5">{profile.record_type_name} · {profile.stage_name || 'No stage'}</p>
        {profile.reference_number && (
          <p className="text-gray-500 text-xs font-mono mt-0.5">{profile.reference_number}</p>
        )}
        {/* Stage Progress Banner */}
        {profile.workflow_stages && profile.workflow_stages.length > 0 && (() => {
          const stages = profile.workflow_stages;
          const currentIdx = stages.findIndex(s => s.is_current);
          let displayStages = stages;
          if (stages.length > 6 && currentIdx >= 0) {
            const pieces = [];
            pieces.push(...stages.slice(0, 2));
            if (currentIdx > 2) pieces.push({ name: '...', _ellipsis: true, id: 'e1' });
            if (currentIdx >= 2) pieces.push(stages[currentIdx]);
            if (currentIdx < stages.length - 2) pieces.push({ name: '...', _ellipsis: true, id: 'e2' });
            if (currentIdx < stages.length - 1) pieces.push(stages[stages.length - 1]);
            const seen = new Set();
            displayStages = pieces.filter(s => { const key = s.id; if (seen.has(key)) return false; seen.add(key); return true; });
          }
          return (
            <div className="flex items-stretch mt-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {displayStages.map((s, i) => {
                if (s._ellipsis) return <div key={s.id} className="flex items-center px-1"><span className="text-gray-600 text-[10px]">•••</span></div>;
                const isCurrent = s.is_current;
                const isPast = currentIdx >= 0 && s.sort_order < stages[currentIdx].sort_order;
                const bgColor = isCurrent ? 'bg-purple-600' : isPast ? 'bg-green-700' : 'bg-gray-800';
                const textColor = isCurrent ? 'text-white font-semibold' : isPast ? 'text-green-100' : 'text-gray-500';
                const isLast = i === displayStages.length - 1 || displayStages[i + 1]?._ellipsis;
                return (
                  <div key={s.id} className="relative flex items-center flex-shrink-0" style={{ marginRight: i < displayStages.length - 1 ? '2px' : '0' }}>
                    <div className={`${bgColor} ${textColor} flex items-center text-[10px] whitespace-nowrap select-none`}
                      style={{ padding: '3px 14px 3px 12px', clipPath: i === 0 ? 'polygon(0 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 0 100%)' : isLast ? 'polygon(6px 0, 100% 0, 100% 100%, 6px 100%, 0 50%)' : 'polygon(6px 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 6px 100%, 0 50%)' }}>
                      {isPast && <span className="mr-1 text-[8px]">✓</span>}
                      {s.name}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Quick stats row */}
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">

          {(profile.comms?.length > 0) && (
            <span onClick={() => setShowCommsModal(true)} className="cursor-pointer text-[10px] px-2 py-1 rounded-full font-medium transition-colors"
              style={{ background: unreadCount > 0 ? '#0c1a2e' : '#1f2937', color: unreadCount > 0 ? '#60a5fa' : '#9ca3af', border: unreadCount > 0 ? '1px solid #1e3a5f' : '1px solid #374151' }}>
              📨 {profile.comms.length} comm{profile.comms.length !== 1 ? 's' : ''}{unreadCount > 0 ? ` · ${unreadCount} unread` : ''}
            </span>
          )}
          {(profile.files?.length > 0) && (
            <span onClick={openDocsModal} className="cursor-pointer text-[10px] px-2 py-1 rounded-full font-medium transition-colors"
              style={{ background: '#1f2937', color: '#9ca3af', border: '1px solid #374151' }}>
              📄 {profile.files.length} doc{profile.files.length !== 1 ? 's' : ''}
            </span>
          )}
          {(profile.parties?.length > 0) && (
            <span className="text-[10px] px-2 py-1 rounded-full font-medium"
              style={{ background: '#1f2937', color: '#9ca3af', border: '1px solid #374151' }}>
              👥 {profile.parties.length} part{profile.parties.length !== 1 ? 'ies' : 'y'}
            </span>
          )}
        </div>
      </div>

      {/* Linked Contact */}
      {profile.linked_contact && (
        <div className="mx-3 mt-3 bg-gray-800/60 rounded-xl p-3 cursor-pointer hover:bg-gray-800 transition-colors flex-shrink-0"
          onClick={() => onSelectRecord && onSelectRecord({ record_id: profile.linked_contact.record_id, title: profile.linked_contact.title, record_type_name: 'Contact' })}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-purple-700 flex items-center justify-center text-[11px] font-bold text-purple-200 flex-shrink-0">
              {(profile.linked_contact.first_name?.[0] || '').toUpperCase()}{(profile.linked_contact.last_name?.[0] || '').toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-200 text-xs font-semibold truncate">{profile.linked_contact.first_name} {profile.linked_contact.last_name}</span>
                {profile.linked_contact.reference_number && <span className="text-gray-500 text-[10px] font-mono flex-shrink-0">{profile.linked_contact.reference_number}</span>}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                {profile.linked_contact.phone && <a href={`tel:${profile.linked_contact.phone}`} onClick={e => e.stopPropagation()} className="text-[10px] text-gray-400 hover:text-purple-300 transition-colors">📞 {profile.linked_contact.phone}</a>}
                {profile.linked_contact.email && <a href={`mailto:${profile.linked_contact.email}`} onClick={e => e.stopPropagation()} className="text-[10px] text-gray-400 hover:text-purple-300 transition-colors truncate">✉ {profile.linked_contact.email}</a>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inbound email */}
      {profile.inbound_email && (
        <div className="mx-3 mt-2 bg-gray-800/60 rounded-xl p-3 flex-shrink-0">
          <p className="text-gray-500 text-[10px] font-medium uppercase tracking-wide mb-1">Inbound Email</p>
          <p className="text-[11px] text-gray-400 font-mono cursor-pointer hover:text-gray-200 transition-colors"
            onClick={() => navigator.clipboard.writeText(profile.inbound_email)} title="Click to copy">{profile.inbound_email}</p>
        </div>
      )}

      {/* ── Accordion (single section open at a time, server-persisted) ── */}
      <LeftPanelAccordion sections={accordionSections} defaultOpenId={savedOpenSection} />

      {/* ── AI FLAGS ── */}
      {Object.keys(profile.extended_data || {}).filter(k => k !== 'last_ai_update' && k !== 'critical_note').length > 0 && (
        <div className="mx-3 mb-3 bg-yellow-950/30 border border-yellow-900/40 rounded-xl p-3 space-y-1.5">
          <p className="text-yellow-400 text-[10px] font-semibold uppercase tracking-wide">AI Flags</p>
          {Object.entries(profile.extended_data).filter(([k]) => k !== 'last_ai_update' && k !== 'critical_note').map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="text-yellow-300/70">{k.replace(/_/g,' ')}</span>
              <span className="text-yellow-200 font-medium">{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Team Access Modal */}
      {showTeamAccessModal && (
        <TeamAccessModal
          recordId={profile.record_id}
          alreadyGrantedTeamIds={(teamAccessList || []).map(t => t.team_id)}
          defaultAccessLevel="write"
          onClose={() => setShowTeamAccessModal(false)}
          onSuccess={() => fetchTeamAccess()}
        />
      )}

      {/* Docs Modal */}
      {showDocsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowDocsModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-xl mx-4 overflow-hidden flex flex-col" style={{ maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 flex-shrink-0">
              <p className="text-sm font-semibold text-white">📄 Documents ({profile.files?.length || 0})</p>
              <button onClick={() => setShowDocsModal(false)} className="text-gray-500 hover:text-gray-300 text-lg leading-none">×</button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {loadingFiles ? <p className="text-xs text-gray-500 text-center py-8">Loading documents...</p>
                : files.length === 0 ? <p className="text-xs text-gray-500 text-center py-8">No documents uploaded yet</p>
                : files.map(f => (
                  <div key={f.id} className="bg-gray-800/60 rounded-xl p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium truncate">{f.original_name}</span>
                      </div>
                      {f.download_url && (
                        <a href={f.download_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex-shrink-0 transition-colors">
                          <Download className="h-3 w-3" /> View
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatDate(f.created_at)}</span>
                      {f.size_bytes && <span>· {formatSize(f.size_bytes)}</span>}
                    </div>
                    {f.document_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {f.document_tags.map(tag => (
                          <span key={tag} className="px-2 py-0.5 text-xs bg-indigo-900/40 border border-indigo-700/40 text-indigo-300 rounded-full">{tag.replace(/_/g,' ')}</span>
                        ))}
                      </div>
                    )}
                    {!f.document_tags?.length && f.document_category && (
                      <span className="px-2 py-0.5 text-xs bg-gray-700 text-gray-400 rounded-full inline-block">{f.document_category.replace(/_/g,' ')}</span>
                    )}
                    {f.ai_summary && <p className="text-xs text-gray-400 italic leading-relaxed border-t border-gray-700/40 pt-2 mt-1">{f.ai_summary}</p>}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Comms Modal */}
      {showCommsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => { setShowCommsModal(false); setCommsSearch(''); }}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full mx-4 overflow-hidden flex flex-col" style={{ maxWidth: '900px', maxHeight: '90vh', width: '90vw' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 flex-shrink-0">
              <p className="text-sm font-semibold text-white">📨 Communications ({profile.comms?.length || 0}){unreadCount > 0 ? ` · ${unreadCount} unread` : ''}</p>
              <button onClick={() => { setShowCommsModal(false); setCommsSearch(''); }} className="text-gray-500 hover:text-gray-300 text-lg leading-none">×</button>
            </div>
            {/* Search */}
            <div className="px-5 py-3 border-b border-gray-800 flex-shrink-0">
              <input
                type="text"
                placeholder="Search communications..."
                value={commsSearch}
                onChange={e => setCommsSearch(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500"
              />
            </div>
            {/* List */}
            <div className="overflow-y-auto p-4 space-y-3">
              {(!profile.comms || profile.comms.length === 0) ? (
                <p className="text-xs text-gray-500 text-center py-8">No communications yet</p>
              ) : (() => {
                const filtered = commsSearch.trim()
                  ? (profile.comms || []).filter(c =>
                      (c.subject || '').toLowerCase().includes(commsSearch.toLowerCase()) ||
                      (c.body || '').toLowerCase().includes(commsSearch.toLowerCase()) ||
                      (c.body_preview || '').toLowerCase().includes(commsSearch.toLowerCase()) ||
                      (c.from_name || '').toLowerCase().includes(commsSearch.toLowerCase())
                    )
                  : (profile.comms || []);
                if (filtered.length === 0) return <p className="text-xs text-gray-500 text-center py-8">No results for "{commsSearch}"</p>;
                return filtered.map(comm => <CommCard key={comm.id} comm={comm} onMarkRead={handleMarkRead} defaultExpanded={true} />);
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Task History Modal */}
      {historyModalTask && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setHistoryModalTask(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-xl mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <div>
                <p className="text-xs font-semibold text-white">#{historyModalTask.task_number} - {historyModalTask.title}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Change History</p>
              </div>
              <button onClick={() => setHistoryModalTask(null)} className="text-gray-500 hover:text-gray-300 text-lg leading-none">×</button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {(historyModalTask.history || []).length === 0 ? (
                <p className="text-xs text-gray-600 italic text-center py-4">No changes recorded yet</p>
              ) : (
                <div className="space-y-2">
                  {(historyModalTask.history || []).map((h, i) => (
                    <div key={h.id || i} className="flex gap-3 py-2 border-b border-gray-800/60 last:border-0">
                      <div className="w-1 bg-purple-700/50 rounded-full flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <span className="text-xs text-purple-300 font-semibold capitalize flex-shrink-0">{h.field_name.replace(/_/g,' ')}</span>
                          <span className="text-[10px] text-gray-500 flex-shrink-0 whitespace-nowrap">{new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <p className="text-[10px] text-gray-400 mb-1.5">{h.first_name} {h.last_name || ''}</p>
                        {h.field_name === 'note_added' ? (
                          <p className="text-xs text-gray-300 italic bg-gray-800/60 rounded px-2 py-1">"{h.new_value}"</p>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {h.old_value && <span className="text-xs text-yellow-400 bg-yellow-900/20 px-2 py-0.5 rounded">{h.old_value}</span>}
                            <span className="text-base font-bold text-gray-300">→</span>
                            <span className="text-xs text-green-400 bg-green-900/20 px-2 py-0.5 rounded">{h.new_value}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommCard({ comm, onMarkRead, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [read, setRead] = useState(comm.is_read);

  const handleExpand = async () => {
    setExpanded(e => !e);
    if (!read && comm.direction === 'inbound') { setRead(true); await onMarkRead(comm.id); }
  };

  const isUnread = !read && comm.direction === 'inbound';
  const channelIcon = comm.channel === 'sms' ? '📱' : comm.channel === 'email' ? '✉️' : comm.channel === 'voice' ? '📞' : '💬';
  const directionLabel = comm.direction === 'inbound' ? '← In' : '→ Out';
  const dateStr = comm.created_at ? new Date(comm.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + new Date(comm.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

  const getBody = () => {
    if (comm.channel === 'sms' && comm.metadata) {
      const m = typeof comm.metadata === 'string' ? JSON.parse(comm.metadata) : comm.metadata;
      const lines = [];
      if (comm.body_preview || comm.body) lines.push(`"${comm.body_preview || comm.body}"`);
      if (m.sms_status) { const s = m.sms_status; lines.push(s === 'delivered' ? '✅ Delivered' : s === 'sent' ? '📤 Sent' : s === 'failed' ? '❌ Failed' : s === 'undelivered' ? `⚠️ Undelivered (${m.error_message || 'unknown'})` : s); }
      if (m.to_number) lines.push(`To: ${m.to_number}`);
      return lines.join(' · ') || 'No content';
    }
    if (comm.channel === 'voice' && comm.metadata) {
      const m = typeof comm.metadata === 'string' ? JSON.parse(comm.metadata) : comm.metadata;
      const lines = [];
      if (m.client_call_status) lines.push(m.client_call_status === 'completed' ? '✅ Answered' : m.client_call_status === 'no-answer' ? '📵 No answer' : m.client_call_status === 'busy' ? '🔴 Busy' : m.client_call_status);
      if (m.call_duration_seconds) lines.push(`${m.call_duration_seconds}s`);
      if (m.worker_name) lines.push(`Worker: ${m.worker_name}`);
      return lines.join(' · ') || 'No details';
    }
    return comm.body || comm.body_preview || 'No content';
  };

  return (
    <div onClick={handleExpand} className="rounded-xl border cursor-pointer transition-all"
      style={{ background: isUnread ? '#0c1a2e' : '#111827', borderColor: isUnread ? '#1e40af' : '#374151' }}>
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          {isUnread && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />}
          <span className="text-base">{channelIcon}</span>
          <span className="text-xs font-semibold" style={{ color: isUnread ? '#93c5fd' : '#9ca3af' }}>
            {directionLabel} · {comm.channel?.toUpperCase()}
          </span>
          <span className="text-sm font-medium text-gray-200 truncate">{comm.subject || comm.from_name || 'No subject'}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isUnread && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: '#1e3a5f', color: '#60a5fa' }}>NEW</span>}
          <span className="text-[11px] text-gray-500">{dateStr}</span>
          <span className="text-gray-600 text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {/* Body — always shown */}
      <div className="px-4 pb-3 text-sm text-gray-300 whitespace-pre-wrap border-t border-gray-800/60 pt-2">
        {getBody()}
      </div>
    </div>
  );
}
